import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Grid,
  TextField,
  Button,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Backdrop,
  CircularProgress,
  Alert,
} from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
// import { useNavigate } from "react-router-dom";
import useLabInternalStore from "../../store/useLabInternalStore";
import { useEffect } from "react";
import useProfileAuthStore from "../../store/useAuthStore";
import useAuthStore from "../../store/authStore";
import { formatAcademicDashboardChipLabel } from "../../utils/academicProfileFromApi";
import { SUBMISSION_BRANCH } from "../../config/github";
import { formatDateTimeIST } from "../../utils/istDateTime";

const emptyLab = {
  title: "",
  deadline: "",
  status: "",
  repoUrl: "",
  branch: "",
  submissionStatus: "NOT_SUBMITTED",
  questionsLocked: false,
  questionsLockedAt: null,
  labId: "",
  _id:0
};

const statusChipProps = (status) => {
  switch (status) {
    case "NOT_SUBMITTED":
      return { label: "Not Submitted", color: "default" };
    case "SUBMITTED":
      return { label: "Submitted", color: "primary" };
    case "EVALUATED":
      return { label: "Evaluated", color: "success" };
    default:
      return { label: status, color: "default" };
  }
};

const StudentLabDashboard = () => {
  // const navigate = useNavigate();
  const user = useProfileAuthStore((s) => s.user);
  const { academicProfile } = useAuthStore();
  const fetchStudentLabInternals = useLabInternalStore((s) => s.fetchStudentLabInternals);
  const fetchStudentLabDashboard = useLabInternalStore((s) => s.fetchStudentLabDashboard);
  const fetchStudentLabQuestions = useLabInternalStore((s) => s.fetchStudentLabQuestions);
  const lockLabQuestions = useLabInternalStore((s) => s.lockStudentLabQuestions);
  const submitTest = useLabInternalStore((s) => s.submitStudentTest);
  const [lab, setLab] = React.useState(emptyLab);
  // eslint-disable-next-line no-unused-vars
  const [regdId, setRegdId] = React.useState("");
  const [evaluation, setEvaluation] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [questions, setQuestions] = React.useState([]);
  const [questionsLoading, setQuestionsLoading] = React.useState(false);
  const [questionsError, setQuestionsError] = React.useState("");
  const [selectedQuestionId, setSelectedQuestionId] = React.useState("");
  const [selectedQId, setSelectedQId] = React.useState("null");
  const [selectedQuestion, setSelectedQuestion] = React.useState(null);
  const [expanded, setExpanded] = React.useState(false);
  const [lockDialogOpen, setLockDialogOpen] = React.useState(false);
  const [lockLoading, setLockLoading] = React.useState(false);
  const [lockError, setLockError] = React.useState("");
  const [lockedQuestionId, setLockedQuestionId] = React.useState("");
  
  // New states for submit test
  const [repoUrl, setRepoUrl] = React.useState("");
  const [branch, setBranch] = React.useState(SUBMISSION_BRANCH);
  const [submitLoading, setSubmitLoading] = React.useState(false);
  const [submitError, setSubmitError] = React.useState("");
  const [submitSuccess, setSubmitSuccess] = React.useState(false);

  const displayedQuestions = React.useMemo(() => {
    if (!questions.length) return questions;

    if (!lab.questionsLocked) {
      return questions;
    }

    const priorityId = selectedQuestionId || lockedQuestionId;
    if (!priorityId) return [];

    const keyMatches = (q) => (q.questionId || q._id) === priorityId;
    const selected = questions.find(keyMatches);
    return selected ? [selected] : [];
  }, [questions, lab.questionsLocked, selectedQuestionId, lockedQuestionId]);

  React.useEffect(() => {
    let isMounted = true;

    const loadData = async () => {
      try {
        setLoading(true);
        setError("");
        await fetchStudentLabInternals();
        const labs = useLabInternalStore.getState().studentLabInternals || [];

        if (!labs.length) {
          if (isMounted) {
            setLoading(false);
          }
          return;
        }

        const labData = labs[0] || {};
        const evaluationData = labData?.evaluation || null;

        const dashboard = await fetchStudentLabDashboard(labData._id);
        const questionData = dashboard?.data || dashboard || {};

        if (!isMounted) {
          return;
        }

        const normalizedLab = {
          title:
            labData?.title || "Lab",
          labId: labData?.labId || "",
          deadline: labData?.endDate || labData?.lab?.endDate || labData?.deadline || "",
          startDate: labData?.startDate || labData?.lab?.startDate || "",
          status: labData?.lab?.status || labData?.labStatus || "",
          repoUrl:
            labData?.studentLab?.repo?.url ||
            labData?.githubRepoUrl ||
            labData?.repoUrl ||
            "",
          branch:
            labData?.studentLab?.repo?.branch ||
            labData?.submissionBranch ||
            labData?.branch ||
            "",
          submissionStatus:
            questionData?.studentState?.submissionStatus ||
            "NOT_SUBMITTED",
          questionsLocked: Boolean(
             questionData?.studentState?.questionsLocked,
          ),
          questionsLockedAt:
            labData?.studentLab?.questionsLockedAt ||
            labData?.questionsLockedAt ||
            null,
          _id: labData?._id || "",
        };
        
        setLab(normalizedLab);
        setEvaluation(evaluationData || null);
        setLockedQuestionId(
          questionData?.studentState?.selectedQuestionId ||
          questionData?.studentState?.questionId ||
          ""
        );
        // Initialize repo URL and branch from lab data
        setRepoUrl(normalizedLab.repoUrl || "");
        setBranch(normalizedLab.branch || SUBMISSION_BRANCH);
        // Set regdId directly from auth store
        setRegdId(user?.id || 0);
      } catch (err) {
        if (isMounted) {
          setError("Unable to load lab details.");
          // console.error("Error loading lab details:", err);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadData();

    return () => {
      isMounted = false;
    };
  }, [user, fetchStudentLabInternals, fetchStudentLabDashboard]);

  React.useEffect(() => {
    let isMounted = true;

    const loadQuestions = async () => {
      setQuestionsLoading(true);
      setQuestionsError("");
      try {
        const labId = lab.labId;
        await fetchStudentLabQuestions(labId);
        const data = useLabInternalStore.getState().studentLabQuestions[labId];
        const allowedQuestions = data?.allowedQuestions || data || [];
        if (isMounted) {
          setQuestions(Array.isArray(allowedQuestions) ? allowedQuestions : []);
        }
      } catch (err) {
        if (isMounted) {
          setQuestionsError("Unable to load questions.");
        }
      } finally {
        if (isMounted) {
          setQuestionsLoading(false);
        }
      }
    };
    if (lab.labId) {
      loadQuestions();
    }

    return () => {
      isMounted = false;
    };
  }, [lab.labId, fetchStudentLabQuestions]);

  useEffect(() => {
    if (!lab.questionsLocked || !lockedQuestionId || !questions.length) return;
    if (selectedQuestionId) return;

    const match = questions.find(q =>
      q.questionId === lockedQuestionId || q._id === lockedQuestionId
    );
    if (match) {
      const questionKey = match.questionId || match._id;
      setExpanded(questionKey);
      setSelectedQuestionId(questionKey);
      setSelectedQId(match._id);
      setSelectedQuestion({ ...match, parts: match.parts || {} });
    }
  }, [lab.questionsLocked, lockedQuestionId, questions, selectedQuestionId]);

  const deadlineDate = lab.deadline ? new Date(lab.deadline) : null;
  const formattedDeadline = formatDateTimeIST(deadlineDate, "", { withZone: true });
  const isAfterDeadline = deadlineDate
    ? Date.now() > deadlineDate.getTime()
    : false;
  const isSelectionLocked =
    lab.questionsLocked;
  const chipProps = statusChipProps(lab.submissionStatus);
  const evaluationCompleted = evaluation?.status === "COMPLETED";

  const handleAccordionChange = (questionKey, entry) => (event, isExpanded) => {
    setExpanded(isExpanded ? questionKey : false);
    if (isExpanded) {
      setSelectedQuestionId(questionKey);
      setSelectedQId( entry._id);
      setSelectedQuestion({ ...entry, parts: entry.parts || {} });
    } else {
      setSelectedQuestionId("");
      setSelectedQId("");
      setSelectedQuestion(null);
    }
  };

  const handleOpenLockDialog = () => {
    if (!selectedQuestionId) return;
    setLockDialogOpen(true);
  };

  const handleCloseLockDialog = () => {
    if (!lockLoading) {
      setLockDialogOpen(false);
    }
  };

  const handleConfirmLock = async () => {
    if (!selectedQuestionId) return;
    setLockLoading(true);
    setLockError("");

    try {
      const labId = lab.labId;
      // console.log("Testing lockLabQuestions with", labId, { questionId: selectedQId }, lab._id);
      const response = await lockLabQuestions(labId, { questionId: selectedQId });

      // console.log("handleConfirmLock", response?.data?.labId, response, selectedQId);
      const dashboard = await fetchStudentLabDashboard(response?.data?.labId);
      const updated = dashboard?.data|| dashboard?.studentLab;
      if (updated) {
        setLab((prev) => ({
          ...prev,
          questionsLocked: updated?.studentState?.questionsLocked || updated.questionsLocked,
          questionsLockedAt: updated?.studentState?.questionsLockedAt || updated.questionsLockedAt,
          submissionStatus: updated?.studentState?.submissionStatus || prev.submissionStatus,
        }));
      } else {
        setLab((prev) => ({ ...prev, questionsLocked: true }));
      }
      setLockDialogOpen(false);
    } catch (err) {
      // console.log("Error locking question selection:", err)
      setLockError(
        err?.response?.data?.message || "Unable to lock question selection.",
      );
    } finally {
      setLockLoading(false);
      setLockDialogOpen(false);
    }
  };

  const handleSubmit = async () => {
    setSubmitError("");
    setSubmitSuccess(false);

    // Validation
    if (!repoUrl) {
      setSubmitError("GitHub repository URL is required.");
      return;
    }

    if (!branch) {
      setSubmitError("Branch name is required.");
      return;
    }

    // Validate GitHub URL format
    const githubRegex = /^https?:\/\/(www\.)?github\.com\/[\w-]+\/[\w.-]+(\/)?$/;
    if (!githubRegex.test(repoUrl)) {
      setSubmitError("Please enter a valid GitHub repository URL.");
      return;
    }

    if (!lab.questionsLocked) {
      setSubmitError("You must lock a question before submitting the test.");
      return;
    }

    setSubmitLoading(true);

    try {
      const response = await submitTest(lab.labId, {
        githubRepoUrl: repoUrl,
        branch: branch,
      });

      if (response.success) {
        setSubmitSuccess(true);
        // Update lab status
        
        // Redirect after 1.5 seconds
        setTimeout(() => {
          setLab((prev) => ({
            ...prev,
            submissionStatus: response?.data?.submission ? "SUBMITTED" : prev.submissionStatus,
            repoUrl: response?.data?.submission?.repoUrl || prev.repoUrl
          }));
          // navigate("/student/ide/:labInternalId/:questionId".replace(":labInternalId", lab._id).replace(":questionId", selectedQId))
          // navigate("/student/ide/" + lab._id + "/" + selectedQId);
        }, 1500);
      } else {
        setSubmitError(response.message || "Failed to submit test.");
      }
    } catch (err) {
      // console.error("Submit error:", err);
      setSubmitError(
        err?.response?.data?.message || "Failed to submit test. Please try again."
      );
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Backdrop open={loading} sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}>
        <CircularProgress color="inherit" />
      </Backdrop>

      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 1.5,
          mb: 2,
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Student Lab Dashboard
        </Typography>
        <Chip
          size="small"
          variant="outlined"
          label={formatAcademicDashboardChipLabel(academicProfile)}
        />
      </Box>
      {loading && (
        <Typography variant="body2" sx={{ mb: 2 }}>
          Loading lab details...
        </Typography>
      )}
      {!loading && !error && !lab._id && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          No lab has been configured yet. Check back later.
        </Typography>
      )}
      {error && (
        <Typography variant="body2" color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      <Grid container spacing={2} sx={{ mb: 3, width: '100%' }}>
        <Grid item xs={12} md={12}>
          <Card elevation={1} >
            <CardContent>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
              >
                Lab Information
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {lab.title}
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                Deadline (IST): {formattedDeadline}
              </Typography>
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                Status: {lab.status}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                <Chip size="small" {...chipProps} />
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Card elevation={1} sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            {lab.questionsLocked ? 'Selected Question' : 'Question Selection'}
          </Typography>
          {questionsLoading && (
            <Typography variant="body2" sx={{ mb: 1 }}>
              Loading questions...
            </Typography>
          )}
          {questionsError && (
            <Typography variant="body2" color="error" sx={{ mb: 1 }}>
              {questionsError}
            </Typography>
          )}
          <Stack spacing={2}>
            {displayedQuestions.map((entry) => {
              const questionKey = entry.questionId || entry._id;
              const isSelected = selectedQuestionId === questionKey;
              return (
                <Accordion
                  key={questionKey}
                  expanded={expanded === questionKey}
                  onChange={handleAccordionChange(questionKey, entry)}
                  sx={(theme) => ({
                    border: isSelected
                      ? `2px solid ${theme.palette.primary.main}`
                      : `1px solid ${theme.palette.divider}`,
                    backgroundColor: isSelected
                      ? theme.palette.action.selected
                      : theme.palette.background.paper,
                    color: theme.palette.text.primary,
                    opacity: isSelectionLocked || isAfterDeadline ? 0.6 : 1,
                    boxShadow: 'none',
                    transition: 'border 0.2s, background-color 0.2s',
                    '&:before': { display: 'none' },
                  })}
                  disabled={isSelectionLocked || isAfterDeadline}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls={`panel-content-${questionKey}`}
                    id={`panel-header-${questionKey}`}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {entry.title || "Untitled Question"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Difficulty: {entry.difficulty || "-"}
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {entry.description || "No description provided."}
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Card
                          elevation={0}
                          sx={(theme) => ({
                            border: "1px solid",
                            borderColor: "divider",
                            backgroundColor: theme.palette.background.default,
                            color: theme.palette.text.primary
                          })}
                        >
                          <CardContent>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                             Part 1
                            </Typography>
                            <Typography variant="body2">
                              {entry.parts?.part1?.description || "No description provided."}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Card
                          elevation={0}
                          sx={(theme) => ({
                            border: "1px solid",
                            borderColor: "divider",
                            backgroundColor: theme.palette.background.default,
                            color: theme.palette.text.primary
                          })}
                        >
                          <CardContent>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                              Part 2
                            </Typography>
                            <Typography variant="body2">
                              {entry.parts?.part2?.description || "No description provided."}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Stack>

          {lockError && (
            <Typography variant="body2" color="error" sx={{ mt: 2 }}>
              {lockError}
            </Typography>
          )}

          {!lab.questionsLocked && <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
            <Button
              variant="contained"
              onClick={handleOpenLockDialog}
              disabled={
                !selectedQuestionId || isSelectionLocked || isAfterDeadline
              }
            >
              Lock Selection
            </Button>
            {(isSelectionLocked || isAfterDeadline) && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ alignSelf: "center" }}
              >
                {isAfterDeadline
                  ? "Selection closed (deadline passed)."
                  : "Selection locked."}
              </Typography>
            )}
          </Stack>}
        </CardContent>
      </Card>

      {(lab?.submissionStatus === 'NOT_SUBMITTED') && (
        <Card elevation={1} sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Repository Details
            </Typography>
            {submitError && <Alert severity="error" sx={{ mb: 2 }}>{submitError}</Alert>}
          {submitSuccess && <Alert severity="success" sx={{ mb: 2 }}>Test submitted successfully!</Alert>}
          <Stack spacing={2} direction={{ xs: "column", sm: "row" }}>
            <TextField
              label="GitHub Repo URL"
              fullWidth
              value={repoUrl}
              onChange={(e) => setRepoUrl(e.target.value)}
              disabled={submitLoading || isAfterDeadline}
              placeholder="https://github.com/username/repository"
              error={!!submitError && !repoUrl}
            />
            <TextField
              label="Branch"
              fullWidth
              value={branch}
              onChange={(e) => setBranch(e.target.value)}
              disabled={submitLoading}
            />
          </Stack>
        </CardContent>
      </Card>)}

      {evaluationCompleted && (
        <Card elevation={1}>
          <CardContent>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Evaluation Results
            </Typography>
            <Stack spacing={2} direction={{ xs: "column", md: "row" }}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Selected Question
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {selectedQuestion?.title || "-"}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Part1 Score
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {evaluation?.part1Marks ?? 0} /{" "}
                  {selectedQuestion?.allowedMarks?.part1MaxMarks ?? "-"}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Part2 Score
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {evaluation?.part2Marks ?? 0} /{" "}
                  {selectedQuestion?.allowedMarks?.part2MaxMarks ?? "-"}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Total Score
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {evaluation?.totalScore ?? 0}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Evaluation Status
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  {evaluation?.status || "-"}
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      )}

      <Dialog open={lockDialogOpen} onClose={handleCloseLockDialog}>
        <DialogTitle>Lock Question Selection</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mt: 1 }}>
            You are about to lock your selected question. You will not be able
            to change it after this.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseLockDialog} disabled={lockLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmLock}
            disabled={lockLoading}
          >
            Confirm & Lock
          </Button>
        </DialogActions>
      </Dialog>

      {lab?.submissionStatus === 'NOT_SUBMITTED' && (
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={submitLoading || !repoUrl || !branch || isAfterDeadline}
        >
          {submitLoading ? <CircularProgress size={20} sx={{ mr: 1 }} /> : ''}
          Start Test
        </Button>
      </Box>)}
    </Box>
  );
};

export default StudentLabDashboard;
